import { useState } from 'react';
import { ApiError, api, formatDateId } from '../api/client';
import { useSession } from '../lib/session';
import { localizeMessage, useLocale, useT } from '../i18n/locale';
import { Alert, Button, Card, Field, Input, Modal, PasswordInput } from '../components/ui';

interface DeleteSummary {
  scheduledFor: string;
  graceDays: number;
  receipts: number;
  drafts: number;
  users: number;
  message: string;
  messageCode?: string;
}

/**
 * Account deletion, owner only.
 *
 * Two deliberate frictions: the password is re-entered, and the company name
 * must be typed. Nothing here can be done by reflex, because after the grace
 * period the receipts are gone.
 */
export default function DangerZone() {
  const { tenant, refresh } = useSession();
  const t = useT();
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<DeleteSummary | null>(null);

  const closing = Boolean(tenant?.deletionScheduledFor);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<DeleteSummary>('/account/delete', { password, confirmName });
      setDone(res);
      setPassword('');
      setConfirmName('');
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('danger.scheduleFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/account/delete/cancel');
      setDone(null);
      setOpen(false);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('danger.cancelFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (closing) {
    return (
      <Card className="border-red-300 p-5">
        <h3 className="m-0 mb-3 text-base font-bold text-red-700">{t('danger.scheduledTitle')}</h3>
        <Alert tone="error">
          {t('danger.scheduledAlertPre')}
          <b>{formatDateId(tenant!.deletionScheduledFor!)}</b>
          {t('danger.scheduledAlertPost')}
        </Alert>
        {error ? (
          <div className="mt-3">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        <div className="mt-4">
          <Button onClick={() => void undo()} disabled={busy}>
            {busy ? t('danger.cancelling') : t('danger.cancelDeletion')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-red-200 p-5">
        <h3 className="m-0 mb-1 text-base font-bold text-red-700">{t('danger.heading')}</h3>
        <p className="m-0 mb-4 text-sm text-slate-600">{t('danger.intro')}</p>
        <Button variant="danger" onClick={() => setOpen(true)}>
          {t('danger.deleteButton')}
        </Button>
      </Card>

      <Modal
        open={open}
        title={t('danger.deleteTitle')}
        onClose={() => !busy && setOpen(false)}
        footer={
          done ? (
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => void submit()}
                disabled={busy || !password || !confirmName}
              >
                {busy ? t('common.processing') : t('danger.confirmDelete')}
              </Button>
            </>
          )
        }
      >
        {done ? (
          <Alert tone="warn">
            {localizeMessage(locale, done.messageCode, done.message, {
              date: formatDateId(done.scheduledFor),
            })}
          </Alert>
        ) : (
          <>
            <Alert tone="error">
              {t('danger.warnPre')}
              <b>{t('danger.warnBold')}</b>
              {t('danger.warnPost')}
            </Alert>

            <p className="mt-4 mb-0 text-sm text-slate-600">{t('danger.immediate')}</p>

            {error ? (
              <div className="mt-4">
                <Alert tone="error">{error}</Alert>
              </div>
            ) : null}

            <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
              <Field label={t('danger.passwordLabel')} hint={t('danger.passwordHint')}>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </Field>
              <Field
                label={t('danger.confirmNameLabel')}
                hint={t('danger.confirmNameHint', { name: tenant?.name ?? '' })}
              >
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={tenant?.name ?? ''}
                />
              </Field>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
